import { useEffect } from 'react';
import { Field, Formik } from 'formik';
import { Button, Modal } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import images from '../../assets/images';
import FormikInputWithIcon from '../../components/reactstrap-formik/FormikInputWithIcon';
import type { AppState } from '../../store/state/app.state';
import { companyNameReplace } from '../../utils/index';
import { useReferralPromotionPreference } from './ReferralPromotionPreferenceHook';

const ReferralPromotionPreference = () => {
  const { t } = useTranslation();
  const {
    fetchData,
    checkReferralCodeValid,
    handleSubmit,
    checkPromotionCodeValid,
    initialValues,
    isReferralBonusOff,
    isValidReferralCode,
    isValidPromotionCode,
    promotionCodeErrorMessage,
    showMarketingConsentPopup,
    savePreference,
    handleFocus,
  } = useReferralPromotionPreference();

  useEffect(() => {
    fetchData();
  }, []);

  const userData = useSelector((state: AppState) => state.UserData);

  return (
    <>
      <Helmet>
        <title>{companyNameReplace(t('referralPromotionPreference.title'))} </title>
      </Helmet>
      <div className="login-page container-fluid p-0 font-poppins">
        <div className="row h-100 no-gutters">
          <div className="col-md login-background-block">
            <a href="#" title={companyNameReplace(t<string>('login.label.logo'))} className="instavolt-logo">
              <img src={images.logoPNG} alt={companyNameReplace(t<string>('login.label.logo'))} />
            </a>
          </div>
          <div className="col-md-auto login-section">
            <div className="login-outer">
              <div className="login-contain">
                <div className="title-outer mb-4">
                  <div className="d-flex justify-content-center align-items-center">
                    <div className="sign-up-icon-container">
                      <img className="sign-up-icon" src={images.UserIcon} alt={t<string>('navbar.title')} />
                    </div>
                  </div>
                  <h1 className="title sign-up-title mt-5 mb-2">
                    {`${t('referralPromotionPreference.label.welcome')} ${userData.firstName ?? ''}`}
                  </h1>

                  <div className="referral-promotion-confirmation-message">
                    {t('referralPromotionPreference.label.referralPromotionConfirmationMessage')}
                  </div>
                </div>

                <Formik enableReinitialize initialValues={initialValues} onSubmit={(values) => handleSubmit(values)}>
                  {({ values, handleSubmit }) => (
                    <form onSubmit={handleSubmit} autoComplete="off">
                      {isReferralBonusOff === false && (
                        <>
                          <div className="apply-text-outer success">
                            <Field
                              id="referralCode"
                              name="referralCode"
                              component={FormikInputWithIcon}
                              placeholder={t('referralPromotionPreference.placeholder.referralCode')}
                              imageSource={images.smallUserIcon}
                              onBlur={() => {
                                checkReferralCodeValid(values);
                              }}
                              onFocus={(event) => handleFocus(event)}
                            />
                            {isValidReferralCode !== null && isValidReferralCode && (
                              <span className="applied">
                                <img className="icon" src={images.appliedSVG} alt="" />
                              </span>
                            )}
                          </div>
                          <div className="mt-1 pl-3">
                            {isValidReferralCode !== null && !isValidReferralCode && (
                              <span className="apply-text">{t('termsAndConditionListing.label.invalidCode')}</span>
                            )}
                          </div>
                        </>
                      )}

                      <div className="apply-text-outer success mt-3">
                        <Field
                          id="promotionCode"
                          name="promotionCode"
                          component={FormikInputWithIcon}
                          placeholder={t('referralPromotionPreference.placeholder.promotionCode')}
                          imageSource={images.RewardIcon}
                          onBlur={() => {
                            checkPromotionCodeValid(values);
                          }}
                          onFocus={(event) => handleFocus(event)}
                        />

                        {isValidPromotionCode !== null && isValidPromotionCode && (
                          <span className="applied">
                            <img className="icon" src={images.appliedSVG} alt="" />
                          </span>
                        )}
                      </div>
                      <div className="mt-1 pl-3">
                        {isValidPromotionCode !== null && !isValidPromotionCode && (
                          <span className="apply-text mb-3">{promotionCodeErrorMessage}</span>
                        )}
                      </div>

                      <p className="mt-4 marketing-preference-update-message">
                        <span style={{ fontWeight: 500 }}>
                          {t('referralPromotionPreference.label.marketingPreferenceKeepUp')}
                        </span>{' '}
                        {t('referralPromotionPreference.label.marketingPreferenceUpdateMessage')}
                      </p>

                      <div className="clearfix">
                        <div className="custom-control custom-control-inline pr-4 mt-2 square-radio">
                          <Field
                            type="radio"
                            id="yes"
                            value="true"
                            name="isMarketingConsentApplied"
                            className="custom-control-input"
                          />
                          <label className="custom-control-label marketing-consent-label" htmlFor="yes">
                            {t('referralPromotionPreference.marketingConsentModal.button.yes')}
                          </label>
                        </div>
                        <div className="custom-control custom-control-inline square-radio">
                          <Field
                            type="radio"
                            id="no"
                            value="false"
                            name="isMarketingConsentApplied"
                            className="custom-control-input"
                          />
                          <label className="custom-control-label marketing-consent-label" htmlFor="no">
                            {t('referralPromotionPreference.marketingConsentModal.button.no')}
                          </label>
                        </div>
                      </div>

                      <div className="w-100 text-center">
                        <button type="submit" className="btn btn-primary mt-5 auth-app-button">
                          {t('referralPromotionPreference.marketingConsentModal.button.next')}
                        </button>
                      </div>
                    </form>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Modal show={showMarketingConsentPopup} className="font-poppins contact-preference-modal" centered>
        <Modal.Body className="modalBody">
          <div className="marketing-consent-title my-2">
            {' '}
            {t('referralPromotionPreference.marketingConsentModal.contactPreferences')}
          </div>

          <p className="p-0 pt-2 marketing-preference-prompt">
            {t('referralPromotionPreference.marketingConsentModal.marketingFeaturePrompt')}
          </p>
        </Modal.Body>
        <Modal.Footer className="p-0">
          <div className="row w-100 px-5 mb-3">
            <div className="col-6">
              <Button
                className="btn btn-primary w-100 marketing-consent-button"
                onClick={() => {
                  savePreference(true);
                }}
                title={t<string>('referralPromotionPreference.marketingConsentModal.button.yes')}
              >
                <span>{t('referralPromotionPreference.marketingConsentModal.button.yes')}</span>
              </Button>
            </div>
            <div className="col-6">
              <Button
                className="btn btn-secondary w-100 marketing-consent-button"
                onClick={() => {
                  savePreference(false);
                }}
                title={t<string>('referralPromotionPreference.marketingConsentModal.button.no')}
              >
                <span>{t('referralPromotionPreference.marketingConsentModal.button.no')}</span>
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};
export default ReferralPromotionPreference;
